/* eslint-disable react/jsx-props-no-spreading */
import React, {FC, Key, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {ConversationActionContextsEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {
  InterfaceEventsEnum,
  TimelineActivityActionsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {InlineLinkButton} from '../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  ArchivedDecisionTreeVisualBasicActivity,
  ConversationStageUpdateByCompanyVisualBasicActivity,
  ConversationStageUpdateByRuleVisualBasicActivity,
  CustomAttributeUpdatedVisualBasicActivity,
  TaggedVisualBasicActivity,
  TopicAddedVisualBasicActivity,
  TopicClosedVisualBasicActivity,
  TopicRemovedVisualBasicActivity,
  UnTaggedVisualBasicActivity,
  VisualBasicActivity,
  VisualBasicActivityTypesEnum,
} from '../../../../../../srcCommon/inboxes/viewer/activity/visualActivity/visualBasicActivity';
import {isTopicCreatedViaReferenceViewer} from '../../../../../helpers/models/topicHelpers';
import {
  ActivityTopicTypesEnum,
  BasicActivityModel,
  BasicActivityTypesEnum,
  BasicVisualTypesEnum,
} from '../../../../../models/api/activityModel';
import {
  ActivitySourceTypesEnum,
  ActivitySourceVisualTypesEnum,
} from '../../../../../models/api/activitySourceModel';
import {IntegrationModel} from '../../../../../models/api/integrationModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {TopicModel} from '../../../../../models/api/topicModel';
import {buildPublicId} from '../../../../../models/applications/publicIdTypes';
import {BasicViewerGroupHeaderContainerProps} from '../../../../../store/data/activities/activitiesCollapseSelectors';
import {RulesAppliedEducationTipCoordinator} from '../../../../onboarding/sandbox/educationTips/rulesAppliedEducationTipCoordinator';
import {ActivityApplicationForTopicPill} from '../activityApplicationForTopicPill/activityApplicationForTopicPill';
import {ActivityConversationStageUpdatePopover} from '../activityConversationStageUpdate/activityConversationStageUpdatePopover';
import {ActivityCustomFieldPill} from '../activityCustomFieldPill/activityCustomFieldPill';
import {ActivityDecisionTreePill} from '../activityDecisionTreePill/activityDecisionTreePill';
import {ActivityReferencePreviewerPill} from '../activityReferencePreviewerPill/activityReferencePreviewerPill';
import {ActivityTagPillContainer} from '../activityTagPill/activityTagPillContainer';
import {ActivityTopicPillContainer} from '../activityTopicPill/activityTopicPillContainer';
import {ActivityWithIcon} from '../activityWithIcon/activityWithIcon';
import {ActivityWithRuleIcon, computeRuleIconName} from '../activityWithIcon/activityWithRuleIcon';
import {
  ActivitySource,
  ApiActivitySource,
  OutOfOfficeActivitySource,
  renderTeammateNameOrYou,
  RuleActivitySource,
  TeammateActivitySource,
} from './activitySource';
import {BasicActivityErrorViewerContainer} from './basicActivityErrorViewerContainer';
import {BasicGroupHeaderContainer} from './basicGroupHeaderContainer';
import {BasicViewerActionSkipped} from './basicViewerActionSkipped';
import {BasicViewerBase} from './basicViewerBase';
import {BasicViewerResnoozeContainer} from './basicViewerResnoozeContainer';

/*
 * Props.
 */

export interface BasicViewerProps extends BasicViewerGroupHeaderContainerProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  user: TeammateModel;
  userId: number;
  activity: BasicActivityModel;
  activityIndex: number;
  focusKey: Key | undefined;
  isPreviousActivityBasic: boolean;
  sourceTeammate?: TeammateModel;
  visualBasicActivity: VisualBasicActivity | undefined;
  namespace: string;
  navigateToLink: (arg: string) => void;
  isFirstOfGroup: boolean;
  isExpanded: boolean;
  topicIntegration?: IntegrationModel;
  conversationId: number;
  canceledReminder?: ReminderModel;
  conversationActionContext: ConversationActionContextsEnum | undefined;
  conversationMessageType: MessageTypesEnum;
}

/*
 * Style.
 */

const StyledCapitalizeSpan = styled.span`
  text-transform: capitalize;
`;

const StyledReferencePreviewerPillWrapperDiv = styled.div`
  display: inline-flex;
  flex-flow: row;
  gap: 4px;
  align-items: center;
`;

/*
 * Component.
 */

export const BasicViewer: FC<PropsWithChildren<BasicViewerProps>> = (props) => (
  <>
    {props.isFirstOfGroup && (
      <BasicGroupHeaderContainer
        activityIndex={props.activityIndex}
        focusKey={props.focusKey}
        activityId={props.activity.id}
        conversationId={props.activity.conversationId}
        panelId={props.panelId}
        activities={props.activities}
        isExpanded={props.isExpanded}
      />
    )}
    {props.isExpanded && (
      <BasicViewerBase
        isPreviousActivityBasic={props.isFirstOfGroup || props.isPreviousActivityBasic}
        date={props.activity.updatedAt}
      >
        <RulesAppliedEducationTipCoordinator activity={props.activity}>
          {renderVisualActivity(props)}
        </RulesAppliedEducationTipCoordinator>
      </BasicViewerBase>
    )}
    {maybeRenderResnoozeNotification(props)}
  </>
);

/*
 * Content.
 */

function maybeRenderResnoozeNotification(props: BasicViewerProps) {
  const {canceledReminder, cellsContext, conversationId, conversationActionContext} = props;
  if (!conversationActionContext || !canceledReminder) {
    return null;
  }

  return (
    <BasicViewerResnoozeContainer
      canceledReminder={canceledReminder}
      cellsContext={cellsContext}
      conversationId={conversationId}
      conversationActionContext={conversationActionContext}
      isPreviousActivityBasic={props.isPreviousActivityBasic}
    />
  );
}

function renderVisualActivity(props: BasicViewerProps) {
  const {userId, visualBasicActivity: visualActivity, panelId, conversationMessageType: messageType} = props;
  if (!visualActivity) {
    return null;
  }
  const splitText = (
    <strong>
      {messageType === MessageTypesEnum.EMAIL ? (
        <Trans id="activity-basic-message-moved">Message moved</Trans>
      ) : (
        <Trans id="activity-basic-message-split">Messages split</Trans>
      )}
    </strong>
  );

  switch (visualActivity.type) {
    case VisualBasicActivityTypesEnum.ASSIGNED_USER_THEMSELVES:
      return (
        <ActivityWithIcon iconName="assignActivity">
          <Trans id="activity-basic-assign-you-self">
            <strong>Assigned</strong> to yourself
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_TEAMMATE_THEMSELVES:
      return (
        <ActivityWithIcon iconName="assignActivity">
          <Trans id="activity-basic-assign-teammate-self">
            <strong>Assigned</strong> to {renderTeammateNameOrYou(userId, visualActivity.assignee)} by
            themselves
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="assignActivity">
          <Trans id="activity-basic-assign-teammate">
            <strong>Assigned</strong> to {renderTeammateNameOrYou(userId, visualActivity.assignee)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_BY_RULE:
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-basic-assign-rule">
            <strong>Assigned</strong> to {renderTeammateNameOrYou(userId, visualActivity.assignee)} by
          </Trans>{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_ASSIGNMENT}
          />
        </ActivityWithRuleIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_BY_API:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-assign-api">
            <strong>Assigned</strong> to {renderTeammateNameOrYou(userId, visualActivity.assignee)} by
          </Trans>{' '}
          <ApiActivitySource activitySource={visualActivity.source} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_OTHER:
      return (
        <Trans id="activity-basic-old-assign">
          This conversation was assigned to {buildTeammateDisplayName(visualActivity.assignee)}{' '}
          <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.MOVED_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="moveActivity">
          <Trans id="activity-basic-move-teammate">
            <strong>Moved</strong> to {visualActivity.inbox.name} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.MOVED_BY_RULE:
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-basic-move-rule">
            <strong>Moved to {visualActivity.inbox.name}</strong> by
          </Trans>{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_MOVE}
          />
        </ActivityWithRuleIcon>
      );

    case VisualBasicActivityTypesEnum.MOVED_BY_OTHER:
      return (
        <Trans id="activity-basic-old-move">
          This conversation was moved to {visualActivity.inbox.name} <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.TAG:
      return renderTagActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.UNTAG:
      return renderUnTagActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.TOPIC_CLOSED:
      return renderTopicClosedActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.TOPIC_ADDED:
      return renderTopicAddedActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.TOPIC_REMOVED:
      return renderTopicRemovedActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.CUSTOM_ATTRIBUTE_UPDATED:
      return renderCustomAttributeUpdatedActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.ERROR:
      return (
        <BasicActivityErrorViewerContainer
          activityError={visualActivity.activityError}
          activitySource={visualActivity.activitySource}
          namespace={props.namespace}
        />
      );

    case VisualBasicActivityTypesEnum.FORWARDED_BY_RULE:
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-basic-forward-rule">
            <strong>Forwarded</strong> to {visualActivity.contactHandle} by
          </Trans>{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_FORWARD}
          />
        </ActivityWithRuleIcon>
      );

    case VisualBasicActivityTypesEnum.FORWARDED_BY_OTHER:
      return (
        <Trans id="activity-basic-old-forward">
          This conversation was forwarded to {visualActivity.contactHandle} <ActivitySource {...props} />
        </Trans>
      );

    // The only variant of tracker_archive we want to render (only when archived by a rule + you can't see other teammates' activities).
    case VisualBasicActivityTypesEnum.TRACKER_ARCHIVE_USER_BY_RULE:
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-basic-tracker-archive-user-rule">
            <strong>Archived for you</strong> by
          </Trans>{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_ARCHIVE}
          />
        </ActivityWithRuleIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_USER_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="inviteActivity">
          <Trans id="activity-tracker-share-teammate-to-you">
            <strong>You were invited</strong> by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="inviteActivity">
          <Trans id="activity-tracker-share-teammate-to-teammate">
            <strong>
              <StyledCapitalizeSpan>
                {renderTeammateNameOrYou(userId, visualActivity.recipient)}
              </StyledCapitalizeSpan>{' '}
              was invited
            </strong>{' '}
            by <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_RULE:
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-tracker-share-rule">
            <strong>Notified</strong> {renderTeammateNameOrYou(userId, visualActivity.recipient)} by
          </Trans>{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_NOTIFY_TEAMMATE}
          />
        </ActivityWithRuleIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_DELETED_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-tracker-share-rule">
            <strong>Notified</strong> {renderTeammateNameOrYou(userId, visualActivity.recipient)} by
          </Trans>{' '}
          a deleted rule.
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_OTHER:
      return (
        <Trans id="activity-tracker-old-share">
          {buildTeammateDisplayName(visualActivity.recipient)} was added to the conversation{' '}
          <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.TRACKER_UNSHARE_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="removeActivity">
          <Trans id="activity-tracker-unshare-by-teammate">
            <strong>
              <StyledCapitalizeSpan>
                {renderTeammateNameOrYou(userId, visualActivity.recipient)}
              </StyledCapitalizeSpan>{' '}
              was removed
            </strong>{' '}
            by <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_UNSHARE_USER_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="removeActivity">
          <Trans id="activity-tracker-unshare-user-by-teammate">
            <strong>You were removed</strong> by{' '}
            <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_INACTIVITY:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-inactivity">
            <strong>Automatically unassigned</strong> due to inactivity
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_USER:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-teammate-you">
            <strong>Unassigned</strong>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_TEAMMATE:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-teammate">
            <strong>Unassigned</strong> by{' '}
            <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_OOO:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-ooo">
            <strong>Automatically unassigned</strong>
          </Trans>{' '}
          <OutOfOfficeActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_DELETED_TEAMMATE:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-deleted">
            <strong>Automatically unassigned</strong> because the teammate is no longer available
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_OTHER:
      return (
        <Trans id="activity-basic-old-unassign">
          This conversation was unassigned <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_TEAMMATE:
      return (
        <ActivityWithIcon iconName="snoozeActivity">
          <Trans id="activity-basic-archive-snooze">
            <strong>Snoozed</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_RULE:
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-basic-archive-snooze-rule">
            <strong>Snoozed</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_SNOOZE}
          />
        </ActivityWithRuleIcon>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_OTHER:
      return (
        <Trans id="activity-basic-old-archive-snooze">
          This conversation was snoozed <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_TEAMMATE:
      return (
        <ActivityWithIcon iconName="archiveActivity">
          <Trans id="activity-basic-archive-teammate">
            <strong>Archived</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_RULE:
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-basic-archive-rule">
            <strong>Archived</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_ARCHIVE}
          />
        </ActivityWithRuleIcon>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_DECISION_TREE:
      return renderArchiveActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.ARCHIVED_OTHER:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-archive-other">
            <strong>Archived</strong> <ActivitySource {...props} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.REOPEN_USER:
      return (
        <ActivityWithIcon iconName="inboxActivity">
          <Trans id="activity-basic-reopen-you">
            <strong>Reopened</strong>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.REOPEN_TEAMMATE:
      return (
        <ActivityWithIcon iconName="inboxActivity">
          <Trans id="activity-basic-reopen-teammate">
            <strong>Reopened</strong> by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.REOPEN_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-reopen-rule">
            <strong>Reopened</strong> by a rule
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.REOPEN_MESSAGE_SENT:
      return (
        <ActivityWithIcon iconName="inboxActivity">
          <Trans id="activity-basic-reopen-sending">
            <strong>Reopened</strong>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.REOPEN_OTHER:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-reopen-other">
            <strong>Reopened</strong> <ActivitySource {...props} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRASHED_TEAMMATE:
      return (
        <ActivityWithIcon iconName="trashActivity">
          <Trans id="activity-basic-trash">
            <strong>Trashed</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRASHED_SPAM:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-trash-spamfilter">
            <strong>Trashed</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by spam
            filtering
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRASHED_DATA_RETENTION:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-trash-dataretention">
            <strong>Trashed</strong> by data retention policy
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRASHED_OTHER:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-trash-other">
            <strong>Trashed</strong> <ActivitySource {...props} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.MERGE_TEAMMATE:
      return (
        <ActivityWithIcon iconName="mergeActivity">
          <Trans id="activity-basic-merge">
            <strong>Conversation merged</strong> with another conversation by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.MERGE_OTHER:
      return (
        <Trans id="activity-basic-old-merge">
          This conversation was merged with another <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.RESTORE_TEAMMATE:
      return (
        <ActivityWithIcon iconName="inboxActivity">
          <Trans id="activity-basic-restore">
            <strong>Restored</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.RESTORE_OTHER:
      return (
        <Trans id="activity-basic-old-restore">
          This conversation was restored <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.RULE_NOTIFIED_EXISTING_RULE:
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-basic-rulenotify-withrule">
            <strong>Notified</strong> you by
          </Trans>{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_NOTIFY_YOU}
          />
        </ActivityWithRuleIcon>
      );

    case VisualBasicActivityTypesEnum.RULE_NOTIFIED_DELETED_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-rulenotify-withoutrule">
            A rule <strong>notified</strong> you
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.RULE_NOTIFIED_OTHER:
      return (
        <Trans id="activity-basic-old-rulenotify">
          You were notified <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.REMINDER_EXPIRED:
      return (
        <ActivityWithIcon iconName="snoozeActivity">
          <Trans id="activity-basic-reminder">
            <strong>Snooze expired</strong>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.SPLIT_ORIGIN_TEAMMATE:
      return (
        <ActivityWithIcon iconName="splitActivity">
          <Trans id="activity-basic-split-origin">
            {splitText} to <ActivityConversationLink {...props}>new conversation</ActivityConversationLink> by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.SPLIT_ORIGIN_RULE:
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-basic-split-origin">
            {splitText} to <ActivityConversationLink {...props}>new conversation</ActivityConversationLink> by
          </Trans>{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_SPLIT}
          />
        </ActivityWithRuleIcon>
      );

    case VisualBasicActivityTypesEnum.SPLIT_ORIGIN_DATA_LEAK:
      return (
        <ActivityWithIcon iconName="splitActivity">
          <Trans id="activity-basic-split-origin-privacy">
            {splitText} to a <ActivityConversationLink {...props}>new conversation</ActivityConversationLink>{' '}
            for privacy reasons
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.SPLIT_ORIGIN_OTHER:
      return (
        <ActivityWithIcon iconName="splitActivity">
          <Trans id="activity-basic-old-split-origin">
            {splitText} to <ActivityConversationLink {...props}>new conversation</ActivityConversationLink>{' '}
            <ActivitySource {...props} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.AUTO_SPLIT_ORIGIN_INACTIVITY:
      return (
        <ActivityWithIcon iconName="splitActivity">
          <Trans id="activity-basic-auto-split-origin">
            <strong>Automatically split inbound</strong> to a{' '}
            <ActivityConversationLink {...props}>new conversation</ActivityConversationLink> after inactivity
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.AUTO_SPLIT_ORIGIN_LINK_CONVERSATION:
      return (
        <ActivityWithIcon iconName="splitActivity">
          <Trans id="activity-basic-auto-split-origin">
            <strong>Conversation split</strong> due to the creation of a{' '}
            <ActivityConversationLink {...props}>linked conversation</ActivityConversationLink>
          </Trans>
        </ActivityWithIcon>
      );
    case VisualBasicActivityTypesEnum.SPLIT_TARGET_RULE:
      return (
        <Trans id="activity-basic-old-split-target">
          <strong>Started</strong> from an{' '}
          <ActivityConversationLink {...props}>existing conversation</ActivityConversationLink> by{' '}
          <RuleActivitySource
            panelId={panelId}
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_SPLIT}
          />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.SPLIT_TARGET_OTHER:
      return (
        <Trans id="activity-basic-old-split-target">
          <strong>Started</strong> from an{' '}
          <ActivityConversationLink {...props}>existing conversation</ActivityConversationLink>{' '}
          <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.FRONT_CHAT_TRANSCRIPT_SENT:
      return (
        <ActivityWithIcon iconName="messageVerySmall">
          <Trans id="activity-basic-front-chat-transcript-sent">
            Front Chat transcript <strong>emailed</strong> to
          </Trans>{' '}
          {visualActivity.transcriptRecipientHandle}
          {' \u2022 '}
          <Trans id="activity-basic-front-chat-transcript-view-email">
            <ActivityConversationLink {...props}>View email</ActivityConversationLink>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.FRONT_CHAT_TRANSCRIPT_EMAIL:
      return (
        <ActivityWithIcon iconName="messageVerySmall">
          <Trans id="activity-basic-front-chat-transcript-email">
            Front Chat transcript <strong>emailed</strong> to
          </Trans>{' '}
          {visualActivity.transcriptRecipientHandle}
          {' \u2022 '}
          <Trans id="activity-basic-front-chat-transcript-view-original-chat">
            <ActivityConversationLink {...props}>View original chat</ActivityConversationLink>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.CONVERSATION_STAGE_UPDATE_BY_COMPANY: {
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-conversation-stage-update">
            <strong>Marked</strong> as{' '}
            <ActivityConversationStageUpdatePopover
              conversationStageType={visualActivity.conversationStageType}
              size={VisualSizesEnum.SMALL}
            />
          </Trans>
        </ActivityWithIcon>
      );
    }
    case VisualBasicActivityTypesEnum.CONVERSATION_STAGE_UPDATE_BY_RULE: {
      const source = renderRelatedResourceSource(props, visualActivity);
      return (
        <ActivityWithRuleIcon ruleBundleType={visualActivity.source.ruleBundleType}>
          <Trans id="activity-basic-conversation-stage-update">
            <strong>Marked</strong> as{' '}
            <ActivityConversationStageUpdatePopover
              conversationStageType={visualActivity.conversationStageType}
              size={VisualSizesEnum.SMALL}
            />
            by {source}
          </Trans>
        </ActivityWithRuleIcon>
      );
    }

    case VisualBasicActivityTypesEnum.CONVERSATION_UNLINKED:
      return (
        <ActivityWithIcon iconName="unlinkedConversationsSmall">
          <Trans id="activity-basic-conversation-unlinked">
            Conversation
            <strong>unlinked</strong> by{' '}
          </Trans>
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ACTION_SKIPPED:
      return <BasicViewerActionSkipped panelId={props.panelId} visualActivity={visualActivity} />;

    default:
      throw new UnreachableError(visualActivity);
  }
}

function renderTagActivity(props: BasicViewerProps, visualActivity: TaggedVisualBasicActivity) {
  const {tag, decisionTreeId, decisionTreeName, decisionTreeNamespace} = visualActivity;

  // Retrieve the tag source (rule, teammate or API).
  const tagSource = renderRelatedResourceSource(props, visualActivity);
  const iconName = computeTagOrTopicActivityIconName(props.activity);

  // If this tag was added by a Chatbot, render the decision tree pill.
  if (decisionTreeId && decisionTreeName && decisionTreeNamespace) {
    return (
      <ActivityWithIcon iconName={iconName}>
        {renderTag(props, tag)}{' '}
        <Trans id="activity-basic-tag-decision-tree">
          <strong>added</strong> by chatbot{' '}
          <ActivityDecisionTreePill
            decisionTreeId={decisionTreeId}
            decisionTreeName={decisionTreeName}
            decisionTreeNamespace={decisionTreeNamespace}
          />
        </Trans>
      </ActivityWithIcon>
    );
  }

  // If we have a tag source available, render that source before the tag.
  if (tagSource) {
    return (
      <ActivityWithIcon iconName={iconName}>
        {renderTag(props, tag)}{' '}
        <Trans id="activity-basic-tag">
          <strong>added</strong> by {tagSource}
        </Trans>
      </ActivityWithIcon>
    );
  }

  // If tag was not from a rule and we cannot determine the tag source, return the old format as a fallback.
  return (
    <Trans id="activity-basic-old-tag">
      {tag.name} was added <ActivitySource {...props} />
    </Trans>
  );
}

function renderUnTagActivity(props: BasicViewerProps, visualActivity: UnTaggedVisualBasicActivity) {
  const {tag} = visualActivity;

  // Retrieve the tag source (rule, teammate or API).
  const tagSource = renderRelatedResourceSource(props, visualActivity);
  const iconName = computeTagOrTopicActivityIconName(props.activity);

  // If we have a tag source available, render that source before the tag.
  if (tagSource) {
    return (
      <ActivityWithIcon iconName={iconName}>
        <Trans id="activity-basic-untag">
          {renderTag(props, tag)} <strong>removed</strong> by {tagSource}
        </Trans>
      </ActivityWithIcon>
    );
  }

  // If tag was not from a rule and we cannot determine the tag source, return the old format as a fallback.
  return (
    <Trans id="activity-basic-old-untag">
      Tag &quot;{tag.name}&quot; was removed <ActivitySource {...props} />
    </Trans>
  );
}

function renderArchiveActivity(
  props: BasicViewerProps,
  visualActivity: ArchivedDecisionTreeVisualBasicActivity,
) {
  const {decisionTreeId, decisionTreeName, decisionTreeNamespace} = visualActivity;

  // Retrieve the tag source (rule, teammate or API).
  const iconName = computeTagOrTopicActivityIconName(props.activity);

  return (
    <ActivityWithIcon iconName={iconName}>
      <Trans id="activity-basic-archive-decision-tree">
        <strong>Archived</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by chatbot{' '}
        <ActivityDecisionTreePill
          decisionTreeId={decisionTreeId}
          decisionTreeName={decisionTreeName}
          decisionTreeNamespace={decisionTreeNamespace}
        />
      </Trans>
    </ActivityWithIcon>
  );
}

function renderTopicClosedActivity(props: BasicViewerProps, visualActivity: TopicClosedVisualBasicActivity) {
  const {topicIntegration} = props;
  const {conversationId} = props.activity;
  const {topic} = visualActivity;

  switch (topic.type) {
    // Handle an issue on JIRA being resolved.
    case ActivityTopicTypesEnum.JIRA: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <ActivityWithIcon iconName="checkmarkYesActivity" iconColor={theme.palette.green.shade40}>
              <Trans id="activity-basic-topic-jira">
                <strong>Resolved</strong> Jira issue
              </Trans>{' '}
              {renderTopicPillContainer(visualActivity, conversationId, {topicIntegration})}
            </ActivityWithIcon>
          )}
        </UserThemeConsumer>
      );
    }
    // Handle an issue on GitHub being closed.
    case ActivityTopicTypesEnum.GITHUB: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <ActivityWithIcon iconName="checkmarkYesActivity" iconColor={theme.palette.green.shade40}>
              <Trans id="activity-basic-topic-github">
                <strong>Closed</strong> Github issue
              </Trans>{' '}
              {renderTopicPillContainer(visualActivity, conversationId, {topicIntegration})}
            </ActivityWithIcon>
          )}
        </UserThemeConsumer>
      );
    }
    // Handle a task on Asana being completed.
    case ActivityTopicTypesEnum.ASANA: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <ActivityWithIcon iconName="checkmarkYesActivity" iconColor={theme.palette.green.shade40}>
              <Trans id="activity-basic-topic-asana">
                <strong>Completed</strong> Asana task
              </Trans>{' '}
              {renderTopicPillContainer(visualActivity, conversationId, {topicIntegration})}
            </ActivityWithIcon>
          )}
        </UserThemeConsumer>
      );
    }
    // Handle a task on Trello being completed.
    case ActivityTopicTypesEnum.TRELLO: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <ActivityWithIcon iconName="checkmarkYesActivity" iconColor={theme.palette.green.shade40}>
              <Trans id="activity-basic-topic-trello">
                <strong>Moved</strong> Trello card to
              </Trans>{' '}
              {renderTopicPillContainer(visualActivity, conversationId, {topicIntegration})}
            </ActivityWithIcon>
          )}
        </UserThemeConsumer>
      );
    }
    default:
      return (
        <Trans id="activity-basic-topic-closed">
          The {renderTopicTypeName(topic, topicIntegration)} issue &quot;
          {topic.name}
          &quot; was closed
        </Trans>
      );
  }
}

function renderTopicAddedActivity(props: BasicViewerProps, visualActivity: TopicAddedVisualBasicActivity) {
  // Retrieve the topic source (rule, teammate or API).
  const topicSource = renderRelatedResourceSource(props, visualActivity);
  const iconName = computeTagOrTopicActivityIconName(props.activity);
  const topicPillOptions = {shouldRemoveLeftMargin: true, topicIntegration: props.topicIntegration};
  const {conversationId} = props.activity;
  const {topic} = visualActivity;

  if (isTopicCreatedViaReferenceViewer(topic)) {
    return (
      <ActivityWithIcon iconName={iconName}>
        <StyledReferencePreviewerPillWrapperDiv>
          <ActivityReferencePreviewerPill
            panelId={props.panelId}
            conversationId={conversationId}
            topicId={topic.id}
          />
          <Trans id="activity-basic-reference-previewer-added">
            <strong>added</strong> by
            <ActivityApplicationForTopicPill topicId={topic.id} />
          </Trans>
        </StyledReferencePreviewerPillWrapperDiv>
      </ActivityWithIcon>
    );
  }

  // If we have a topic source available, render that source with the topic.
  if (topicSource) {
    return (
      <ActivityWithIcon iconName={iconName}>
        {renderTopicPillContainer(visualActivity, conversationId, topicPillOptions)}{' '}
        <Trans id="activity-basic-topic-added">
          <strong>added</strong> by {topicSource}
        </Trans>
      </ActivityWithIcon>
    );
  }

  // If topic was not from a rule and we cannot determine the topic source, return the old format as
  // a fallback (emulating tags).
  return (
    <Trans id="activity-basic-old-topic-added">
      Topic &quot;{topic.name}&quot; was added <ActivitySource {...props} />
    </Trans>
  );
}

function renderTopicRemovedActivity(
  props: BasicViewerProps,
  visualActivity: TopicRemovedVisualBasicActivity,
) {
  // Retrieve the topic source (rule, teammate or API).
  const topicSource = renderRelatedResourceSource(props, visualActivity);
  const iconName = computeTagOrTopicActivityIconName(props.activity);
  const topicPillOptions = {shouldRemoveLeftMargin: true, topicIntegration: props.topicIntegration};
  const {conversationId} = props.activity;
  const {topic} = visualActivity;

  if (isTopicCreatedViaReferenceViewer(topic)) {
    return (
      <ActivityWithIcon iconName={iconName}>
        <StyledReferencePreviewerPillWrapperDiv>
          <ActivityReferencePreviewerPill
            panelId={props.panelId}
            conversationId={conversationId}
            topicId={topic.id}
          />
          <Trans id="activity-basic-reference-previewer-removed">
            <strong>removed</strong> <ActivitySource {...props} />
          </Trans>
        </StyledReferencePreviewerPillWrapperDiv>
      </ActivityWithIcon>
    );
  }

  // If we have a topic source available, render that source with the topic.
  if (topicSource) {
    return (
      <ActivityWithIcon iconName={iconName}>
        {renderTopicPillContainer(visualActivity, conversationId, topicPillOptions)}{' '}
        <Trans id="activity-basic-topic-removed">
          <strong>removed</strong> by {topicSource}
        </Trans>
      </ActivityWithIcon>
    );
  }

  // If topic was not from a rule and we cannot determine the topic source, return the old format as
  // a fallback (emulating tags).
  return (
    <Trans id="activity-basic-old-topic-removed">
      Topic &quot;{topic.name}&quot; was removed <ActivitySource {...props} />
    </Trans>
  );
}

function renderCustomAttributeUpdatedActivity(
  props: BasicViewerProps,
  visualActivity: CustomAttributeUpdatedVisualBasicActivity,
) {
  const {customField} = visualActivity;
  const customAttributeSource = renderRelatedResourceSource(props, visualActivity);
  const iconName = computeCustomAttributeActivityIconName(props.activity);

  if (customAttributeSource) {
    return (
      <ActivityWithIcon iconName={iconName}>
        <ActivityCustomFieldPill
          conversationId={props.conversationId}
          customField={customField}
          teammateId={props.cellsContext.teammateId}
          panelId={props.panelId}
        />
        <Trans id="activity-basic-customattribute">
          <strong>updated</strong> by {customAttributeSource}
        </Trans>
      </ActivityWithIcon>
    );
  }

  return <Trans id="activity-basic-old-customattribute">{customField.name} updated</Trans>;
}

type AddRemoveResourceBasicActivity =
  | TaggedVisualBasicActivity
  | UnTaggedVisualBasicActivity
  | TopicBasedVisualBasicActivity
  | CustomAttributeUpdatedVisualBasicActivity
  | ConversationStageUpdateByCompanyVisualBasicActivity
  | ConversationStageUpdateByRuleVisualBasicActivity;

function renderRelatedResourceSource(
  props: BasicViewerProps,
  visualActivity: AddRemoveResourceBasicActivity,
) {
  const {activity, panelId} = props;
  const {source} = activity;

  // Rule.
  if (
    source.type === ActivitySourceTypesEnum.RULE &&
    source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    const timelineActivityAction =
      activity.type === BasicActivityTypesEnum.TAG
        ? TimelineActivityActionsEnum.CLICK_RULE_TAG
        : TimelineActivityActionsEnum.CLICK_RULE_UNTAG;

    return (
      <RuleActivitySource
        panelId={panelId}
        activitySource={source}
        timelineActivityAction={timelineActivityAction}
      />
    );
  }

  // Teammate.
  if ('sourceTeammate' in visualActivity && visualActivity.sourceTeammate) {
    return <TeammateActivitySource userId={props.userId} sourceTeammate={visualActivity.sourceTeammate} />;
  }

  // Public API.
  if (
    source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return <ApiActivitySource activitySource={source} />;
  }

  return null;
}

interface TopicPillOptions {
  shouldRemoveLeftMargin?: boolean;
  topicIntegration?: IntegrationModel;
}

type TopicBasedVisualBasicActivity =
  | TopicAddedVisualBasicActivity
  | TopicClosedVisualBasicActivity
  | TopicRemovedVisualBasicActivity;

function renderTopicPillContainer(
  activity: TopicBasedVisualBasicActivity,
  conversationId: number,
  options?: TopicPillOptions,
) {
  const {topic} = activity;

  switch (topic.type) {
    // TODO: support any topic type as a pill
    case ActivityTopicTypesEnum.ASANA:
    case ActivityTopicTypesEnum.GITHUB:
    case ActivityTopicTypesEnum.JIRA:
    case ActivityTopicTypesEnum.TRELLO:
    case ActivityTopicTypesEnum.LINEAR:
    case ActivityTopicTypesEnum.MONDAY:
    case ActivityTopicTypesEnum.WEB:
      return (
        <ActivityTopicPillContainer
          conversationId={conversationId}
          topic={topic}
          shouldRemoveLeftMargin={options && options.shouldRemoveLeftMargin}
        />
      );
    default:
      return (
        <Trans id="activity-basic-topic">
          The {renderTopicTypeName(topic, options?.topicIntegration)} link &quot;{topic.name}&quot;
        </Trans>
      );
  }
}

/*
 * Link.
 */

const ActivityConversationLink: FC<PropsWithChildren<BasicViewerProps>> = (props) => {
  const {activity, children} = props;
  const {navigateToLink, maybeRenderLinkError} = useLinkNavigation();

  function maybeRenderLink() {
    if (activity.basicVisualType === BasicVisualTypesEnum.SPLIT_ORIGIN) {
      const targetConversationPublicId = buildPublicId('conversation', activity.targetConversationId);

      const onLinkClick = () => {
        trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
          action: TimelineActivityActionsEnum.CLICK_SPLIT_CONVERSATION_LINK_TO_NEW,
          conversation_id: activity.targetConversationId,
        });

        navigateToLink(targetConversationPublicId);
      };

      return <InlineLinkButton onClick={onLinkClick}>{children}</InlineLinkButton>;
    }

    if (activity.basicVisualType === BasicVisualTypesEnum.SPLIT_TARGET) {
      const originConversationPublicId = buildPublicId('conversation', activity.originConversationId);

      const onLinkClick = () => {
        trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
          action: TimelineActivityActionsEnum.CLICK_SPLIT_CONVERSATION_LINK_TO_ORIGINAL,
          conversation_id: activity.originConversationId,
        });

        navigateToLink(originConversationPublicId);
      };

      return <InlineLinkButton onClick={onLinkClick}>{children}</InlineLinkButton>;
    }

    if (activity.basicVisualType === BasicVisualTypesEnum.FRONT_CHAT_TRANSCRIPT_SENT) {
      const transcriptConversationPublicId = buildPublicId('conversation', activity.frontChatConversationId);

      const onLinkClick = () => {
        trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
          action: TimelineActivityActionsEnum.CLICK_FRONT_CHAT_TRANSCRIPT_LINK_TO_TRANSCRIPT,
          conversation_id: activity.frontChatConversationId,
        });

        navigateToLink(transcriptConversationPublicId);
      };

      return <InlineLinkButton onClick={onLinkClick}>{children}</InlineLinkButton>;
    }

    if (activity.basicVisualType === BasicVisualTypesEnum.FRONT_CHAT_TRANSCRIPT_EMAIL) {
      const transcriptFrontChatConversationPublicId = buildPublicId(
        'conversation',
        activity.frontChatConversationId,
      );

      const onLinkClick = () => {
        trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
          action: TimelineActivityActionsEnum.CLICK_FRONT_CHAT_TRANSCRIPT_LINK_TO_CHAT,
          conversation_id: activity.frontChatConversationId,
        });

        navigateToLink(transcriptFrontChatConversationPublicId);
      };

      return <InlineLinkButton onClick={onLinkClick}>{children}</InlineLinkButton>;
    }

    return null;
  }

  return (
    <>
      {maybeRenderLink()}
      {maybeRenderLinkError()}
    </>
  );
};

/*
 * Helpers.
 */

function renderTopicTypeName(topic: TopicModel, topicIntegration?: IntegrationModel) {
  // If the type is web, we need to translate it.
  if (topic.type === ActivityTopicTypesEnum.WEB) {
    return <Trans id="activity-basic-topic-web">Web</Trans>;
  }
  return topicIntegration?.topicType?.resourceName ?? topicIntegration?.name ?? topic.type;
}

function renderInboxName(originalTeamInboxes: ReadonlyArray<InboxModel>) {
  if (!originalTeamInboxes.length) {
    return undefined;
  }

  if (originalTeamInboxes.length === 1) {
    return originalTeamInboxes[0].name;
  }

  return <Trans id="activity-basic-inbox-multiple">{originalTeamInboxes.length} inboxes</Trans>;
}

/* Render a tag item with its icon, and allow it to be clickable to take the user to their settings. */
function renderTag(props: BasicViewerProps, tag: TagModel) {
  return <ActivityTagPillContainer tag={tag} teammate={props.user} conversationId={props.conversationId} />;
}

/* Determine whether to a tag, untag, or gear icon (for rule or API). */
function computeTagOrTopicActivityIconName(activity: BasicActivityModel): IconName {
  if (
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    return computeRuleIconName(activity.source.ruleBundleType);
  }

  if (
    activity.source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return 'gearActivity';
  }

  if (activity.basicVisualType === BasicVisualTypesEnum.TAG && activity.decisionTreeId) {
    return 'gearActivity';
  }

  switch (activity.type) {
    case BasicActivityTypesEnum.TAG:
      return 'tagActivity';
    case BasicActivityTypesEnum.UNTAG:
      return 'untagActivity';
    case BasicActivityTypesEnum.TOPIC_ADDED:
      return 'integrationActivity';
    case BasicActivityTypesEnum.TOPIC_REMOVED:
      return 'removeIntegrationActivity';
    default:
      return 'gearActivity';
  }
}

function computeCustomAttributeActivityIconName(activity: BasicActivityModel): IconName {
  if (
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    return 'gearActivity';
  }

  if (
    activity.source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return 'gearActivity';
  }

  return 'customFieldsActivity';
}
